import { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import { useSearchParams } from "react-router-dom";
import RawThinkingIndicator from "../components/Chat/ThinkingIndicator";
import RawChatInput from "../components/Chat/ChatInput";
import RawSuggestedQuestions from "../components/Chat/SuggestedQuestions";
import {
  MessageBubble,
  useVisibleChatMessages,
  type ChatInputProps,
  type ChatStreamState,
  type CsvAttachment,
  type ImageAttachment,
  type SuggestedQuestionsProps,
  type ThinkingStep,
} from "../components/Chat";
import { useAuth } from "../hooks/useAuth";
import { ROLES } from "../constants/roles";
import { useChatStream as useRawChatStream, usePbiNotice } from "../hooks";

type ThinkingIndicatorProps = {
  steps: ThinkingStep[];
  agentName: string;
};

const ThinkingIndicator =
  RawThinkingIndicator as unknown as ComponentType<ThinkingIndicatorProps>;
const ChatInput = RawChatInput as unknown as ComponentType<ChatInputProps>;
const SuggestedQuestions =
  RawSuggestedQuestions as unknown as ComponentType<SuggestedQuestionsProps>;

export default function ChatPage() {
  const { user } = useAuth();
  const agentName = user?.agent_name || "Brian";
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationIdParam = searchParams.get("c");
  const pbiParam = searchParams.get("pbi");
  const pbiNotice = usePbiNotice(pbiParam, setSearchParams);

  const { messages, isStreaming, thinkingSteps, error, send } =
    useRawChatStream(conversationIdParam) as unknown as ChatStreamState;
  const visibleMessages = useVisibleChatMessages(messages, thinkingSteps);

  const [input, setInput] = useState("");
  const [images, setImages] = useState<ImageAttachment[]>([]);
  const [csvs, setCsvs] = useState<CsvAttachment[]>([]);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinkingSteps]);

  const handleSend = useCallback(
    (text?: string) => {
      const message = text ?? input;
      const isSuggestedQuestion = typeof text === "string";
      const attachedImages = isSuggestedQuestion ? [] : images;
      const attachedCsvs = isSuggestedQuestion ? [] : csvs;

      if (
        (!message.trim() && !attachedImages.length && !attachedCsvs.length) ||
        isStreaming
      ) {
        return;
      }

      setInput("");
      setImages([]);
      setCsvs([]);
      send({ message, images: attachedImages, csvs: attachedCsvs });
    },
    [input, images, csvs, isStreaming, send],
  );

  const isEmpty = messages.length === 0 && !isStreaming;
  const isViewer = user?.role?.name === ROLES.VIEWER;

  return (
    <div className="flex h-full flex-col">
      {pbiNotice && (
        <div
          className={`mx-3 mt-3 rounded-xl px-4 py-3 text-sm ring-1 md:mx-6 ${
            pbiNotice.type === "success"
              ? "bg-green-50 text-green-700 ring-green-200"
              : "bg-red-50 text-red-600 ring-red-200"
          }`}
        >
          {pbiNotice.text}
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {isEmpty && !isViewer ? (
          <SuggestedQuestions
            agentName={agentName}
            onSelect={(question: string) => {
              setInput(question);
              handleSend(question);
            }}
          />
        ) : (
          <div className="w-full space-y-4 px-3 py-4 md:px-6 md:py-6">
            {visibleMessages.map((message, index) => {
              const question =
                message.role === "assistant"
                  ? visibleMessages
                      .slice(0, index)
                      .reverse()
                      .find((m) => m.role === "user")?.content
                  : undefined;
              return (
                <MessageBubble
                  key={message.id}
                  messageId={message.id}
                  role={message.role}
                  content={message.content}
                  images={message.images}
                  csvs={message.csvs}
                  sql={message.sql}
                  pbiEmbed={message.pbi_embed}
                  comparison={message.comparison}
                  tokensInput={message.tokens_input}
                  tokensOutput={message.tokens_output}
                  isStreaming={message._streaming}
                  onAction={(action: string) => handleSend(action)}
                  agentName={agentName}
                  createdAt={message.createdAt}
                  question={question}
                />
              );
            })}
            {thinkingSteps.length > 0 && isStreaming && (
              <ThinkingIndicator steps={thinkingSteps} agentName={agentName} />
            )}
            {error && (
              <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600 ring-1 ring-red-200">
                {error}
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {isViewer ? (
        <div className="border-t border-gray-200 bg-white px-3 py-4 md:px-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Suggested questions
          </p>
          <SuggestedQuestions
            agentName={agentName}
            onSelect={(question: string) => {
              setInput(question);
              handleSend(question);
            }}
            compact
          />
        </div>
      ) : (
        <ChatInput
          value={input}
          onChange={setInput}
          onSend={() => handleSend()}
          disabled={isStreaming}
          images={images}
          onImagesChange={setImages}
          csvs={csvs}
          onCsvsChange={setCsvs}
        />
      )}
    </div>
  );
}
